/**
 * 로그인 뒤 「운영자 계정 권한을 확인해 주세요(6개월마다)」 창 규칙(T-CONTRACT-1031 §2).
 * GET /perm-reviews/status → { due, dueAt, lastReviewedAt, staff: [{accountId, username, name, menus[], lastLoginAt, inactive90}] }
 * POST /perm-reviews { accountIds } → 기록 후 다음 183일.
 * 탭·세션당 한 번만 묻는다(sessionStorage). 「나중에」도 다음 로그인까지 다시 묻지 않음 — 로그인 성공 때 표시를 지운다.
 * admin-certilife 엔 지원 탭이 없어 지원 탭 예외는 없다.
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(`__tests__/permReview.test.mjs`).
 */
import { formatTime, tokenPayload } from "./mfa.js";

export const REVIEW_TITLE = "운영자 계정 권한을 확인해 주세요(6개월마다)";
export const REVIEW_HINT =
  "운영자 계정과 켜진 메뉴가 지금도 맞는지 확인합니다. 그만둔 사람이나 오래 쓰지 않은 계정이 있으면 「접속 계정 관리」에서 정리한 뒤 「확인했어요」를 누르세요.";

export const REVIEW_ASKED_KEY = "certi.permReview.asked";

// 저장소를 못 쓰는 브라우저(사생활 보호 등)면 묻는 쪽으로(창이 한 번 더 뜨는 것이 안 뜨는 것보다 나음)
const safe = (fn, fallback) => {
  try {
    return fn();
  } catch (e) {
    return fallback;
  }
};
export const alreadyAsked = (storage) => safe(() => !!storage.getItem(REVIEW_ASKED_KEY), false);
export const markAsked = (storage) => safe(() => storage.setItem(REVIEW_ASKED_KEY, "1"));
export const resetAsked = (storage) => safe(() => storage.removeItem(REVIEW_ASKED_KEY));

/** 창을 띄울까: due 가 true 이고 볼 직원이 있을 때만(대상이 아니면 서버가 due false · 빈 목록) */
export const isDue = (status) => !!(status && status.due === true && Array.isArray(status.staff) && status.staff.length);

/**
 * 로그인한 사람 열쇠 — isSelfAccount() 와 같은 기준: 토큰 accountId, 없는 옛 토큰이면 username.
 * 토큰 갱신은 같은 열쇠, 다른 계정·로그아웃은 다른 열쇠(null). 둘 다 없거나 못 읽으면 토큰 글자 그대로
 */
export const sessionKeyOf = (token) => {
  if (!token) return null;
  const p = tokenPayload(String(token));
  if (p && p.accountId) return `a:${p.accountId}`;
  if (p && p.username) return `u:${p.username}`;
  return String(token);
};

/**
 * 로그인 세대: 로그아웃·새 로그인마다 1 올림(토큰 갱신은 그대로). 같은 계정으로 다시 로그인해도
 * 그 전에 보낸 응답이 「나중에」 뒤 창을 다시 열지 않게(10-31 리뷰). 탭(모듈)마다 따로
 */
let generation = 0;
export const nextLoginGeneration = () => ++generation;

/**
 * 상태를 묻고, 띄울 때만 응답을 돌려준다. 요청 중에 로그아웃했거나(세대가 바뀜) 다른 계정으로 로그인했으면 버림(null)
 * — 로그인 화면 위에 이전 계정의 직원 목록이 뜨지 않게(10-31 리뷰)
 */
export const loadDueStatus = async (fetchStatus, currentToken) => {
  const gen = generation;
  const key = sessionKeyOf(currentToken());
  if (!key) return null;
  const status = await fetchStatus();
  return gen === generation && key === sessionKeyOf(currentToken()) && isDue(status) ? status : null;
};

/** 켜진 메뉴 → 큰 메뉴 이름만(「CRM > 캠페인」·「CRM > 발송 현황」 → 「CRM」). 하위 메뉴를 다 늘어놓으면 창에서 읽을 수 없음(10-31 intest I14) */
export const bigMenus = (menus) => {
  const names = Array.isArray(menus) ? menus.map((m) => String(m).split(" > ")[0].trim()).filter(Boolean) : [];
  return names.length ? [...new Set(names)].join(", ") : "-";
};

/** 직원 한 줄 */
export const staffRows = (status) =>
  ((status && status.staff) || []).map((s, i) => ({
    key: s.accountId || String(i),
    name: s.name || "-",
    username: s.username || "-",
    menus: bigMenus(s.menus),
    lastLogin: s.lastLoginAt ? formatTime(s.lastLoginAt) : "기록 없음",
    inactive: !!s.inactive90,
  }));

/** 지난 확인 줄 */
export const lastReviewText = (status) =>
  status && status.lastReviewedAt ? `지난 확인: ${formatTime(status.lastReviewedAt)}` : "처음 확인합니다.";

/** POST /perm-reviews 본문 — 보여 준 직원 */
export const reviewBody = (status) => ({
  accountIds: ((status && status.staff) || []).map((s) => s && s.accountId).filter(Boolean),
});
